import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { getApiHeaders, getApiHeadersWithoutContentType, API_TYPES } from '../utils/apiHeaders';
import { useAPIPerformance } from './usePerformanceMonitor';

const API_BASE_URL = import.meta.env.VITE_DB_API_URL || '/api';

export const useAdminData = () => {
const [subscriptionData, setSubscriptionData] = useState(null);
const [assignedWorkspace, setAssignedWorkspace] = useState(null);
const [availableSubscriptions, setAvailableSubscriptions] = useState(null);
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const { isAuthenticated, getAccessToken, getUserEmail, getUserSubjectId, getIdentityProvider } = useAuth();
const { measureAPICall } = useAPIPerformance();

const getAssignedWorkspace = (subscriptionData) => {
  // Check for both uppercase and lowercase variations
  const workspaces = subscriptionData?.Workspaces || subscriptionData?.workspaces || [];
    
  if (!workspaces || workspaces.length === 0) {
    return null;
  }
    
  // Get the first workspace from the subscription
  const workspace = workspaces[0];
    
  return {
    ...workspace,
    workspaceID: workspace.WorkspaceID || workspace.workspaceID,
    workspaceName: workspace.WorkspaceName || workspace.workspaceName,
    servicePrincipalProfileID: workspace.ServicePrincipalProfileID || workspace.servicePrincipalProfileID,
    reports: workspace.Reports || workspace.reports || [],
    subscriptionInfo: {
      subscriptionID: subscriptionData.SubscriptionID || subscriptionData.subscriptionID,
      orgName: subscriptionData.OrgName || subscriptionData.orgName,
      entraID: subscriptionData.EntraID || subscriptionData.entraID
    }
  };
};

// Update admin identity on first login (similar to PBIE_Viewer logic for users)
const updateAdminIdentity = async (adminEmail) => {
  try {
    const subjectId = getUserSubjectId();
    const identityProvider = getIdentityProvider();
      
    // Only attempt update if we have both OIDC claims
    if (!subjectId || !identityProvider) {
      console.log('Skipping admin identity update - missing OIDC claims');
      return;
    }

    const token = await getAccessToken();
    const requestBody = {
      email: adminEmail,
      identityProvider: identityProvider,
      subjectId: subjectId
    };

    const response = await fetch(`${API_BASE_URL}/UpdateAdminIdentity`, {
      method: 'PUT',
      headers: getApiHeaders(token, API_TYPES.DATABASE),
      body: JSON.stringify(requestBody)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Admin identity updated:', result.Message || result.message);
    } else {
      // Log but don't throw - this is a background operation
      const errorText = await response.text();
      console.warn('Failed to update admin identity:', errorText);
    }
  } catch (err) {
    // Log but don't throw - this is a background operation that shouldn't block the user
    console.warn('Error updating admin identity:', err.message);
  }
};

  const loadAvailableSubscriptions = async () => {
    const email = getUserEmail();
    if (!email) {
      const errorMsg = 'Unable to get user email';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setLoading(true);
    setError(null);
    
    try {
      return await measureAPICall('GetAdminSubscriptions', async () => {
        let token;
        try {
          token = await getAccessToken();
        } catch (tokenError) {
          // Check if this is an authentication error
          if (tokenError.message === 'AUTHENTICATION_REQUIRED') {
            const authErrorMsg = 'AUTHENTICATION_EXPIRED';
            setError(authErrorMsg);
            throw new Error(authErrorMsg);
          }
          throw tokenError;
        }
        
        const apiUrl = `${API_BASE_URL}/GetAdminSubscriptions?adminEmail=${encodeURIComponent(email)}`;
        
        const headers = getApiHeaders(token, API_TYPES.DATABASE);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers
        });

        if (response.ok) {
          const data = await response.json();
          
          // Handle both uppercase and lowercase property names
          const allSubscriptions = data.Subscriptions || data.subscriptions || [];
          
          // Include subscriptions with status "Subscribed" OR "Suspended"
          // This allows admins to view and understand suspended subscriptions
          const activeSubscriptions = allSubscriptions.filter(sub => {
            const status = sub.SaaSSubscriptionStatus || sub.saaSSubscriptionStatus || '';
            return status === 'Subscribed' || status === 'Suspended';
          });
          
          // Check if user has no active or suspended subscriptions
          if (!activeSubscriptions || activeSubscriptions.length === 0) {
            const noSubsError = 'NO_SUBSCRIPTION_FOUND';
            setError(noSubsError);
            setAvailableSubscriptions([]);
            throw new Error(noSubsError);
          }
          
          setAvailableSubscriptions(activeSubscriptions);
          
          return activeSubscriptions;
        } else {
          // Check if this is a 404 (no subscriptions found) - backend returns NotFoundObjectResult
          if (response.status === 404) {
            const noSubsError = 'NO_SUBSCRIPTION_FOUND';
            setError(noSubsError);
            throw new Error(noSubsError);
          }
          
          // Check if this is an authorization error
          if (response.status === 401 || response.status === 403) {
            const authErrorMsg = 'NO_SUBSCRIPTION_FOUND';
            setError(authErrorMsg);
            throw new Error(authErrorMsg);
          }
          
          const errorText = await response.text();
          const errorMsg = `API Error (${response.status}): ${errorText}`;
          setError(errorMsg);
          throw new Error(errorMsg);
        }
      });
    } catch (err) {
      // Check if this is already a user-friendly error message
      if (err.message === 'NO_SUBSCRIPTION_FOUND' || err.message === 'AUTHENTICATION_EXPIRED') {
        setError(err.message);
        throw err;
      }
      
      const errorMsg = `Failed to load available subscriptions: ${err.message}`;
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionData = async (subscriptionId = null) => {
    const email = getUserEmail();
    if (!email) {
      const errorMsg = 'Unable to get user email';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setLoading(true);
    setError(null);
    
    try {
      return await measureAPICall('GetSubscriptionByAdmin', async () => {
        const token = await getAccessToken();
        
        // Build URL with optional subscriptionId parameter
        let apiUrl = `${API_BASE_URL}/GetSubscriptionByAdmin?adminEmail=${encodeURIComponent(email)}`;
        if (subscriptionId) {
          apiUrl += `&subscriptionId=${subscriptionId}`;
        }
        
        const headers = getApiHeaders(token, API_TYPES.DATABASE);
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers
        });

        if (response.ok) {
          const data = await response.json();
          
          setSubscriptionData(data);
          
          const workspace = getAssignedWorkspace(data);
          if (workspace) {
            setAssignedWorkspace(workspace);
          } else {
            // Set a friendly error message
            setError('No workspace found for your subscription. Please contact your administrator to set up a workspace.');
          }
          
          // Update admin identity in background (first login scenario)
          // This matches the PBIE_Viewer logic for users
          updateAdminIdentity(email).catch(err => {
            // Silent failure - identity update is a background operation
            console.warn('Background admin identity update failed:', err);
          });
          
          return data;
        } else {
          const errorText = await response.text();
          const errorMsg = `API Error (${response.status}): ${errorText}`;
          setError(errorMsg);
          throw new Error(errorMsg);
        }
      });
    } catch (err) {
      const errorMsg = `Failed to load subscription data: ${err.message}`;
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      // Load available subscriptions on authentication
      loadAvailableSubscriptions().catch((err) => {
        // Error handled in loadAvailableSubscriptions
      });
    }
  }, [isAuthenticated]);

  return {
    subscriptionData,
    assignedWorkspace,
    availableSubscriptions,
    loading,
    error,
    loadAvailableSubscriptions,
    loadSubscriptionData
  };
};